import React, { useMemo } from "react";
import { Flex, Tooltip } from "antd";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { formatMoney } from "utils/functions";

export interface PaymentBreakdownProps {
  creditor: number;
  commission: number;
  legal: number;
  service: number;
  total?: number;
}

const SLICES = [
  { key: "creditor", label: "Creditor", color: "#3b82f6", bg: "#eff6ff", text: "#1d4ed8" },
  { key: "commission", label: "Commission", color: "#10b981", bg: "#f0fdf4", text: "#047857" },
  { key: "legal", label: "Legal", color: "#ef4444", bg: "#fef2f2", text: "#b91c1c" },
  { key: "service", label: "Service", color: "#f59e0b", bg: "#fffbeb", text: "#b45309" },
] as const;

type SliceKey = (typeof SLICES)[number]["key"];

const pct = (value: number, total: number) => (total > 0 ? ((value / total) * 100).toFixed(1) : "0.0");

const DonutTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const slice = SLICES.find((s) => s.label === entry.name);
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.07)",
        fontFamily: "inherit",
        zIndex: 9999,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: slice?.color,
            display: "inline-block",
          }}
        />
        <span style={{ fontSize: 12, color: "#6b7280" }}>{entry.name}</span>
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{formatMoney(entry.value)}</span>
      <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4 }}>{entry.payload.pct}%</span>
    </div>
  );
};

const BarRow: React.FC<{
  label: string;
  skip?: number;
  value: number;
  total: number;
  color: string;
  bg: string;
  text: string;
}> = ({ label, skip, value, total, color, bg, text }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const skipPercentage = skip && percentage ? Math.floor((skip / total) * 100) : undefined;

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 6,
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: color,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: "#374151" }}>{label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{formatMoney(value, false)}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: text,
              background: bg,
              padding: "1px 6px",
              borderRadius: 99,
            }}
          >
            {pct(value, total)}%
          </span>
        </div>
      </div>

      {/* Track */}
      <div
        style={{
          width: "100%",
          height: 6,
          borderRadius: 99,
          background: "#f3f4f6",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {skipPercentage !== undefined && (
          <div
            style={{
              width: `${skipPercentage}%`,
              height: "100%",
              borderRadius: 99,
              background: "transparent",
              transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
            }}
          />
        )}
        <div
          style={{
            width: `${percentage}%`,
            height: "100%",
            borderRadius: 99,
            background: color,
            transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
      </div>
    </div>
  );
};

export const PaymentBreakdown: React.FC<PaymentBreakdownProps> = ({ creditor, commission, legal, service, total }) => {
  const values: Record<SliceKey, number> = { creditor, commission, legal, service };
  total ??= creditor + commission + legal + service;

  const pieData = useMemo(
    () =>
      SLICES.filter((s) => values[s.key]).map((s) => ({
        name: s.label,
        value: values[s.key],
        pct: pct(values[s.key], total),
        color: s.color,
      })),
    [creditor, commission, legal, service, total],
  );

  const dominant = SLICES.reduce((max, s) => (values[s.key] > values[max.key] ? s : max));

  return (
    <Flex justify="center" align="center">
      <div style={{ width: "100%", height: 180, marginBottom: 20 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={82}
              paddingAngle={2}
              dataKey="value"
              strokeWidth={0}
              isAnimationActive
              animationBegin={0}
              animationDuration={700}
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
              <tspan x="50%" dy="-0.4em" style={{ fontSize: 18, fontWeight: 700, fill: "#111827" }}>
                {pct(values[dominant.key], total)}%
              </tspan>
              <tspan x="50%" dy="1.4em" style={{ fontSize: 10, fill: "#9ca3af" }}>
                {dominant.label}
              </tspan>
            </text>

            <RechartsTooltip content={<DonutTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div>
        {SLICES.filter((s) => values[s.key]).map((s, i) => (
          <BarRow
            key={s.key}
            label={s.label}
            // skip={SLICES.filter((_, idx) => idx < i).reduce((acc, curr) => acc + (values[curr.key] ?? 0), 0)}
            value={values[s.key]}
            total={total}
            color={s.color}
            bg={s.bg}
            text={s.text}
          />
        ))}
      </div>
    </Flex>
  );
};

export default PaymentBreakdown;
